import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';
import {
  customUrlEncode,
  prepareFilters,
  preparePagination,
  prepareSorters,
} from '@/utils/apiParamsUtils';

/**
 * Retrieves all guidelines categories.
 * @returns {Promise<Object[]>} A promise that resolves with an array of guidelines category objects.
 * @throws Will throw an error if the request fails.
 */
export const retrieveAllGuidelinesCategories = async (params) => {
  const filters = prepareFilters(params, {
    name: 'default',
  });

  const sorters = prepareSorters(params, {
    name: 'sortName',
    createdAt: 'sortCreatedAt',
    updatedAt: 'sortUpdatedAt',
  });

  const pagination = preparePagination(params);

  try {
    const response = await axiosClient.get('/categories', {
      params: {
        filter: filters,
        sorter: sorters,
        select: params?.select,
        ...pagination,
      },
      paramsSerializer: (params) => {
        if (params.filter) {
          params.filter = JSON.stringify(params.filter);
        }

        if (params.sorter) {
          params.sorter = JSON.stringify(params.sorter);
        }

        if (params.select) {
          params.select = JSON.stringify(params.select);
        }

        return qs.stringify(params, {
          encode: true,
          arrayFormat: 'brackets',
          encoder: customUrlEncode,
        });
      },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to retrieve guidelines categories:', error);
    throw error;
  }
};

/**
 * Creates a new guideline category.
 * @param {Object} payload - The data for the new guideline category.
 * @returns {Promise<Object>} A promise that resolves with the created guideline category data.
 * @throws Will throw an error if the creation request fails.
 */
export const createNewGuidelineCategory = async (payload) => {
  try {
    const response = await axiosClient.post('/categories/create', payload);
    return response.data;
  } catch (error) {
    console.error('Failed to create a new guideline category:', error);
    throw error;
  }
};

/**
 * Updates an existing guideline category.
 * @param {string} id - The ID of the guideline category to update.
 * @param {Object} payload - The updated data for the guideline category.
 * @returns {Promise<Object>} A promise that resolves with the updated guideline category data.
 * @throws Will throw an error if the update request fails.
 */
export const updateGuidelineCategoryById = async (id, payload) => {
  try {
    const response = await axiosClient.put(`/categories/${id}`, payload);
    return response.data;
  } catch (error) {
    console.error('Failed to update guideline category:', error);
    throw error;
  }
};

/**
 * Deletes a guideline category by its ID.
 * @param {string} id - The ID of the guideline category to delete.
 * @returns {Promise<Object>} A promise that resolves with the response data.
 * @throws Will throw an error if the deletion request fails.
 */
export const deleteGuidelineCategoryById = async (id) => {
  try {
    const response = await axiosClient.delete(`/categories/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to delete guideline category with ID ${id}:`, error);
    throw error;
  }
};

const CategoriesService = {
  retrieveAllGuidelinesCategories,
  createNewGuidelineCategory,
  updateGuidelineCategoryById,
  deleteGuidelineCategoryById,
};

export default CategoriesService;
